/**
 * ClinVea Copilot — the staff AI assistant panel (medopd-docs 154 D6/D7; UI page
 * 02-features/14-ai-features/15-copilot-web-and-app.md).
 *
 * Floating panel from the top bar's AI button (Layout.tsx). Streams answers from POST /api/ai/copilot/chat, shows
 * what it looked up ("Steps"), sources, and change proposals with Confirm / Cancel. Full screen on a phone, and on
 * request on a desktop.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Fade, IconButton, Paper, Stack, TextField, Tooltip, Typography, useMediaQuery,
  useTheme,
} from '@mui/material';
import { ArrowDown, History, Info, Maximize2, Minimize2, Send, Sparkles, Square, SquarePen, User, X } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usePatientDetails } from '../../../contexts/PatientDetailsContext';
import { usePermissions } from '../../../contexts/PermissionContext';
import { useAiModule } from '../../../hooks/useAiModule';
import { CopilotAnswer } from './CopilotAnswer';
import { CopilotHistory, COPILOT_CONVERSATIONS_KEY } from './CopilotHistory';
import { copilotApi } from './copilotApi';
import { builtInSuggestions, suggestionText } from './copilotText';
import { useCopilotChat } from './useCopilotChat';

interface CopilotPanelProps {
  open: boolean;
  onClose: () => void;
}

const NEAR_BOTTOM_PX = 48;

export function CopilotPanel({ open, onClose }: CopilotPanelProps) {
  const { t, i18n } = useTranslation('copilot');
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const { role } = usePermissions();
  const { aiModuleOff } = useAiModule();
  const qc = useQueryClient();
  const chat = useCopilotChat();

  const [input, setInput] = useState('');
  const [view, setView] = useState<'chat' | 'history'>('chat');
  const [expanded, setExpanded] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [atBottom, setAtBottom] = useState(true);
  const [dismissedPatientId, setDismissedPatientId] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fullScreen = isPhone || expanded;
  // White on the dark theme's light blue is too faint: the header and the user's bubbles use the deeper blue there.
  const accent = theme.palette.mode === 'dark' ? 'primary.dark' : 'primary.main';
  const locale = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0];

  // The patient on screen: exactly one patient window open and not minimised. Removable with the chip's ×.
  const { openPatients } = usePatientDetails();
  const visiblePatient = useMemo(() => {
    const visible = Array.from(openPatients.entries()).filter(([, e]) => !e.isMinimized);
    if (visible.length !== 1) return null;
    const [id, entry] = visible[0];
    return { id, name: (entry.patient?.name as string | undefined) || null };
  }, [openPatients]);
  const patient = visiblePatient && visiblePatient.id !== dismissedPatientId ? visiblePatient : null;
  // A patient window sits on the right: the compact panel moves left so both stay visible.
  const patientWindowOpen = useMemo(
    () => Array.from(openPatients.values()).some((e) => !e.isMinimized),
    [openPatients],
  );

  const suggestions = useQuery({
    queryKey: ['copilot', 'suggestions', role, !!patient],
    queryFn: () => copilotApi.suggestions(!!patient),
    enabled: open && !aiModuleOff,
    staleTime: 10 * 60_000,
    retry: false,
  });
  const suggestionItems = useMemo(() => {
    const fromServer = (suggestions.data ?? [])
      .map((s) => suggestionText(s, t, i18n))
      .filter((s): s is string => !!s);
    if (fromServer.length) return fromServer.slice(0, 6);
    return builtInSuggestions(role).map((s) => suggestionText(s, t, i18n)).filter((s): s is string => !!s);
  }, [suggestions.data, role, t, i18n]);

  const focusInput = useCallback(() => {
    window.setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  useEffect(() => {
    if (open && view === 'chat') focusInput();
  }, [open, view, focusInput]);

  // Follow the answer as it streams, unless the user scrolled up to read.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && atBottom) el.scrollTop = el.scrollHeight;
  }, [chat.messages, atBottom]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX);
  };

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    setAtBottom(true);
  };

  const ask = useCallback((text: string) => {
    if (!text.trim() || chat.streaming) return;
    setAtBottom(true);
    void chat.send(text, { patientId: patient?.id ?? null, locale }).then(() => {
      void qc.invalidateQueries({ queryKey: COPILOT_CONVERSATIONS_KEY });
    });
  }, [chat, patient?.id, locale, qc]);

  const submit = () => {
    const text = input.trim();
    if (!text || chat.streaming) return;
    setInput('');
    ask(text);
    focusInput();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  const onPanelKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    if (view === 'history') setView('chat');
    else if (expanded) setExpanded(false);
    else onClose();
  };

  const newChat = () => {
    chat.reset();
    setOpenError(false);
    setView('chat');
    setInput('');
  };

  const openConversation = async (id: string) => {
    setOpenError(false);
    setView('chat');
    try {
      await chat.open(id);
      setAtBottom(true);
    } catch {
      setOpenError(true);
    }
  };

  if (!open) return null;

  const empty = chat.messages.length === 0 && !chat.loadingConversation;

  return (
    <Box
      role="dialog"
      aria-modal={false}
      aria-labelledby="copilot-title"
      onKeyDown={onPanelKeyDown}
      sx={{
        position: 'fixed',
        // Above the patient window (also 1300) so the chat is never hidden behind it.
        zIndex: 1401,
        ...(isPhone
          ? { inset: 0 }
          : expanded
            ? { top: 24, bottom: 24, left: '50%', transform: 'translateX(-50%)', width: 'min(960px, calc(100vw - 48px))' }
            : {
              bottom: 24,
              ...(patientWindowOpen ? { left: 24 } : { right: 24 }),
              width: 420,
              height: 'min(680px, calc(100vh - 96px))',
            }),
      }}
    >
      <Fade in={open}>
        <Paper
          elevation={8}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: isPhone ? 0 : 3,
            border: isPhone ? 0 : '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          {/* Header */}
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 1.5, py: 1, bgcolor: accent, color: 'primary.contrastText' }}>
            <Sparkles size={18} aria-hidden />
            <Typography id="copilot-title" component="h2" variant="subtitle2" sx={{ flex: 1, fontWeight: 600, pl: 0.5 }}>
              {t('title')}
            </Typography>
            <Tooltip title={t('history.title')}>
              <IconButton size="small" sx={{ color: 'inherit' }} aria-label={t('history.title')}
                aria-pressed={view === 'history'} onClick={() => setView((v) => (v === 'history' ? 'chat' : 'history'))}>
                <History size={16} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('actions.newChat')}>
              <IconButton size="small" sx={{ color: 'inherit' }} aria-label={t('actions.newChat')} onClick={newChat}>
                <SquarePen size={16} />
              </IconButton>
            </Tooltip>
            {!isPhone && (
              <Tooltip title={expanded ? t('actions.exitFullScreen') : t('actions.fullScreen')}>
                <IconButton size="small" sx={{ color: 'inherit' }} onClick={() => setExpanded((x) => !x)}
                  aria-label={expanded ? t('actions.exitFullScreen') : t('actions.fullScreen')}>
                  {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={t('actions.close')}>
              <IconButton size="small" sx={{ color: 'inherit' }} aria-label={t('actions.close')} onClick={onClose}>
                <X size={16} />
              </IconButton>
            </Tooltip>
          </Stack>

          {view === 'history' ? (
            <CopilotHistory
              activeId={chat.conversationId}
              onOpen={(id) => { void openConversation(id); }}
              onNew={newChat}
              onBack={() => setView('chat')}
              onDeleted={(id) => { if (id === chat.conversationId) newChat(); }}
            />
          ) : (
            <>
              {patient && (
                <Box sx={{ px: 1.5, pt: 1 }}>
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    icon={<User size={13} />}
                    label={patient.name ? t('patient.about', { name: patient.name }) : t('patient.aboutUnnamed')}
                    onDelete={() => setDismissedPatientId(patient.id)}
                    deleteIcon={<X size={13} aria-label={t('patient.remove')} />}
                    sx={{ maxWidth: '100%' }}
                  />
                </Box>
              )}

              {/* Conversation */}
              <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
                <Box
                  ref={scrollRef}
                  onScroll={onScroll}
                  role="log"
                  aria-live="polite"
                  aria-busy={chat.streaming}
                  aria-label={t('conversation')}
                  sx={{ height: '100%', overflowY: 'auto', px: 1.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}
                >
                  {chat.namesHidden && <Alert severity="info" sx={{ py: 0 }}>{t('history.namesHidden')}</Alert>}
                  {openError && <Alert severity="warning" sx={{ py: 0 }}>{t('history.openFailed')}</Alert>}
                  {chat.loadingConversation && (
                    <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={20} aria-label={t('history.loading')} /></Stack>
                  )}

                  {empty && (
                    <Box sx={{ my: 'auto', textAlign: 'center', px: 1 }}>
                      <Box sx={{ color: 'primary.main', mb: 1 }}><Sparkles size={28} aria-hidden /></Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('empty.title')}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {patient ? t('empty.subtitlePatient') : t('empty.subtitle')}
                      </Typography>
                      {suggestionItems.length > 0 && (
                        <Stack spacing={0.75} sx={{ mt: 2, alignItems: 'stretch' }} aria-label={t('empty.suggestions')} role="list">
                          {suggestionItems.map((s) => (
                            <Box role="listitem" key={s}>
                              <Chip
                                label={s}
                                onClick={() => ask(s)}
                                variant="outlined"
                                sx={{ height: 'auto', width: '100%', justifyContent: 'flex-start', py: 0.75,
                                  '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'left' } }}
                              />
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  )}

                  {chat.messages.map((m) => (
                    m.role === 'user' ? (
                      <Box key={m.localId} sx={{ alignSelf: 'flex-end', maxWidth: '85%' }}>
                        <Paper elevation={0} sx={{ px: 1.5, py: 1, borderRadius: '12px 12px 2px 12px', bgcolor: accent, color: 'primary.contrastText' }}>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.content}</Typography>
                        </Paper>
                      </Box>
                    ) : (
                      <Box key={m.localId} sx={{ alignSelf: 'stretch', maxWidth: fullScreen ? 820 : '100%', width: '100%', mx: fullScreen ? 'auto' : 0 }}>
                        <CopilotAnswer
                          message={m}
                          conversationId={chat.conversationId}
                          onRetry={chat.streaming ? undefined : ask}
                          onFeedback={(v) => chat.setFeedback(m.localId, v)}
                        />
                      </Box>
                    )
                  ))}
                </Box>
                {!atBottom && chat.messages.length > 0 && (
                  <Tooltip title={t('actions.jumpToLatest')}>
                    <IconButton
                      size="small"
                      onClick={scrollToBottom}
                      aria-label={t('actions.jumpToLatest')}
                      sx={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', bgcolor: 'background.paper',
                        boxShadow: 2, '&:hover': { bgcolor: 'background.paper' } }}
                    >
                      <ArrowDown size={16} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              {/* Composer */}
              <Box sx={{ px: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  <TextField
                    inputRef={inputRef}
                    fullWidth
                    multiline
                    maxRows={6}
                    size="small"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={t('composer.placeholder')}
                    inputProps={{ 'aria-label': t('composer.label'), maxLength: 4000 }}
                  />
                  {chat.streaming ? (
                    <Tooltip title={t('actions.stop')}>
                      <IconButton color="primary" onClick={chat.stop} aria-label={t('actions.stop')} sx={{ mb: 0.25 }}>
                        <Square size={16} fill="currentColor" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title={t('actions.send')}>
                      <span>
                        <IconButton color="primary" onClick={submit} disabled={!input.trim()} aria-label={t('actions.send')} sx={{ mb: 0.25 }}>
                          <Send size={16} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ py: 0.75, color: 'text.secondary' }}>
                  <Info size={11} aria-hidden style={{ flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ lineHeight: 1.3 }}>{t('footer')}</Typography>
                </Stack>
              </Box>
            </>
          )}
        </Paper>
      </Fade>
    </Box>
  );
}
